import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Crown, Loader2, User } from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../App'; // Import useAuth hook

const cn = (...classes) => classes.filter(Boolean).join(' ');

const SidebarButton = ({ icon: Icon, label, isActive, onClick, badge = null }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex items-center w-full px-4 py-3 mb-2 transition-all duration-300 rounded-xl group",
      isActive 
        ? "bg-blue-100 text-[#253985]" 
        : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
    )}
  >
    <div className="relative mr-3">
      <Icon className={cn(
        "h-5 w-5",
        isActive ? "text-[#253985]" : "text-gray-400 group-hover:text-gray-700"
      )} />
      {badge && (
        <span className="absolute -top-1.5 -right-2.5 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
          {badge}
        </span>
      )}
    </div>
    <span className="font-medium">{label}</span>
    {isActive && (
      <div className="ml-auto w-1.5 h-6 bg-[#253985] rounded-full" />
    )}
  </button>
);

const DashboardSidebar = ({ isOpen, toggleSidebar, navItems, currentPath, navigate }) => {
  const { t } = useTranslation();
  const { user, httpClient } = useAuth(); // Get user and httpClient from auth context
  
  const [isPremium, setIsPremium] = useState(false);
  const [isLoadingPremium, setIsLoadingPremium] = useState(true);

  // Fetch premium status using JWT authentication
  const fetchPremiumStatus = async () => {
    try {
      setIsLoadingPremium(true);
      
      // Use httpClient from auth context (handles JWT token automatically)
      const response = await httpClient.request('/api/user/premium-status');
      
      if (response.ok) {
        const data = await response.json();
        setIsPremium(data.isPremium);
      } else {
        console.error('Failed to fetch premium status:', response.status);
        setIsPremium(false);
      }
    } catch (error) {
      console.error('Error fetching premium status:', error);
      setIsPremium(false);
    } finally {
      setIsLoadingPremium(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchPremiumStatus();
    }
  }, [isOpen, user]);

  return (
    <div className="relative">
      <div className={`h-full bg-white border-r border-gray-200 transition-all duration-300 ${
        isOpen ? 'w-64' : 'w-0 overflow-hidden'
      }`}>
        {isOpen && (
          <div className="flex flex-col h-full">
            <div className="px-4 py-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="relative w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-[#253985]" />
                  {isPremium && !isLoadingPremium && (
                    <div className="absolute -top-1 -right-1 bg-amber-500 rounded-full p-0.5">
                      <Crown className="h-3 w-3 text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('sidebar.welcome')}</p>
                  <h3 className="font-semibold text-[#253985]">
                    {user?.name || user?.userName || t('sidebar.welcomeBack')}
                  </h3>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-3">
              <div className="space-y-1">
                {navItems.map((item) => (
                  <SidebarButton
                    key={item.path}
                    icon={item.icon}
                    label={item.label}
                    badge={item.badge}
                    isActive={currentPath === item.path}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
              {isLoadingPremium ? (
                <div className="bg-gradient-to-r from-blue-50 to-amber-50 p-4 rounded-lg">
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-5 w-5 text-[#253985] animate-spin mr-2" />
                    <span className="text-sm font-medium text-gray-900">Checking status...</span>
                  </div>
                </div>
              ) : isPremium ? (
                <div className="bg-gradient-to-r from-blue-50 to-amber-50 p-4 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Crown className="h-5 w-5 text-amber-500 mr-2" />
                    <span className="text-sm font-semibold text-[#253985]">EasySubs Premium</span>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-blue-50 to-amber-50 p-4 rounded-lg">
                  <div className="flex items-center justify-center mb-2">
                    <Crown className="h-5 w-5 text-amber-500 mr-2" />
                    <span className="text-sm font-medium text-gray-900">{t('sidebar.proFeatures')}</span>
                  </div>
                  <button
                    onClick={() => navigate("/upgrade")}
                    className="w-full py-2.5 bg-gradient-to-r from-[#253985] to-amber-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {t('sidebar.upgradeNow')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={toggleSidebar}
        className={`absolute top-1/2 -translate-y-1/2 z-50 p-2 bg-white rounded-full shadow-md border border-gray-200 hover:bg-gray-50 transition-all ${
          isOpen ? 'left-[calc(100%-12px)]' : 'left-0'
        }`}
      >
        {isOpen ? <FiChevronLeft /> : <FiChevronRight />}
      </button>
    </div>
  );
};

export default DashboardSidebar;